import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { Footer } from "../components/Footer";

export function ConfirmDeletionPage() {
	const { workspaceSlug } = useParams<{ workspaceSlug: string }>();
	const [searchParams] = useSearchParams();
	const token = searchParams.get("token");
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState(false);

	async function handleConfirm() {
		setSubmitting(true);
		setError(null);
		try {
			const res = await fetch(
				`/api/tenant/${workspaceSlug}/confirm-deletion?token=${token}`,
			);
			if (!res.ok) {
				const body = (await res.json().catch(() => ({}))) as { error?: string };
				if (body.error === "expired") {
					setError("This deletion link has expired. Please request a new one.");
				} else if (body.error === "already-completed") {
					setError("This account has already been deleted.");
				} else if (body.error === "not-found") {
					setError("Invalid deletion link.");
				} else {
					setError("Failed to delete account. Please try again.");
				}
				return;
			}
			setSuccess(true);
		} catch {
			setError("Network error. Please try again.");
		} finally {
			setSubmitting(false);
		}
	}

	if (!token) {
		return (
			<div className="min-h-screen flex flex-col bg-gray-50">
				<div className="flex-1 flex items-center justify-center">
					<div className="max-w-sm w-full p-6 bg-white rounded-lg shadow">
						<h1 className="text-xl font-semibold mb-4">Invalid Link</h1>
						<p className="text-sm text-gray-600">
							This deletion link is missing a token. Please use the link from
							your confirmation email.
						</p>
					</div>
				</div>
				<Footer />
			</div>
		);
	}

	if (success) {
		return (
			<div className="min-h-screen flex flex-col bg-gray-50">
				<div className="flex-1 flex items-center justify-center">
					<div className="max-w-sm w-full p-6 bg-white rounded-lg shadow">
						<h1 className="text-xl font-semibold mb-4">Account Deleted</h1>
						<p className="text-sm text-gray-600 mb-6">
							Your account and all associated data have been permanently
							deleted.
						</p>
						<Link
							to={`/${workspaceSlug}/sign-up`}
							className="block w-full text-center bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700"
						>
							Create a new account
						</Link>
					</div>
				</div>
				<Footer />
			</div>
		);
	}

	return (
		<div className="min-h-screen flex flex-col bg-gray-50">
			<div className="flex-1 flex items-center justify-center">
				<div className="max-w-sm w-full p-6 bg-white rounded-lg shadow">
					<h1 className="text-xl font-semibold mb-4">
						Confirm Account Deletion
					</h1>
					<p className="text-sm text-gray-600 mb-6">
						You are about to permanently delete your account and all associated
						data. This action cannot be undone.
					</p>
					{error && <p className="text-sm text-red-600 mb-4">{error}</p>}
					<button
						type="button"
						onClick={handleConfirm}
						disabled={submitting}
						className="w-full bg-red-600 text-white py-2 px-4 rounded hover:bg-red-700 disabled:opacity-50"
					>
						{submitting ? "Deleting..." : "Confirm Deletion"}
					</button>
				</div>
			</div>
			<Footer />
		</div>
	);
}
